/*
Шаблон промпта для мастера «Настроить ИИ-агента».
Пользователь выбирает тип бизнеса и отвечает на вопросы, а роль, правила и тон добавляем сами.
Ответы идут в конец промпта после «О компании:» строками «Подпись: ответ», поэтому при повторном
входе в мастер их можно разобрать обратно по полям (parseAgentPrompt).
*/

export type AgentTone = "formal" | "friendly";

export const TONES: Array<{ value: AgentTone; label: string; hint: string; line: string }> = [
  {
    value: "formal",
    label: "На «вы»",
    hint: "Вежливо и сдержанно",
    line: "Обращайся к клиенту на «вы», вежливо и сдержанно.",
  },
  {
    value: "friendly",
    label: "На «ты»",
    hint: "Дружелюбно и просто",
    line: "Общайся дружелюбно и просто, на «ты».",
  },
];

// ---------- Вопросы по типам бизнеса ----------

export interface BusinessField {
  key: string;
  // Подпись поля — она же подпись строки в промпте. Без двоеточий, уникальна в шаблоне
  label: string;
  // Вопрос, который видит пользователь
  question: string;
  placeholder: string;
  required?: boolean;
  multiline?: boolean;
  hint?: string;
  suggestions?: string[];
}

export type BusinessKey = "course" | "school" | "marketing" | "beauty" | "shop" | "clinic" | "travel" | "other";

/*
role, goal, rules — уходят в скрытую часть промпта, пользователь их не видит.
fields — вопросы мастера; «Чего нельзя обещать» и «Дополнительно» добавляются ко всем.
questions — частые вопросы клиентов для тестового чата.
*/
export interface BusinessTemplate {
  key: BusinessKey;
  label: string;
  emoji: string;
  role?: string;
  goal?: string;
  rules?: string[];
  fields: BusinessField[];
  forbiddenPlaceholder: string;
  questions: string[];
}

const PRICE_HINT = "Длинный прайс удобнее загрузить в «Базу знаний» агента — здесь хватит основных позиций.";

// Готовые варианты ответов — кнопки под полем. Нажатие добавляет вариант в ответ, повторное — убирает
const PAYMENT = ["Kaspi", "Kaspi QR", "Наличные", "Банковская карта", "Перевод"];
const SCHEDULE = ["Ежедневно с 10:00 до 21:00", "Пн–пт с 9:00 до 18:00", "Пн–сб с 9:00 до 20:00", "Без выходных", "Круглосуточно"];
const REFUND = ["Возврат в течение 14 дней", "Возврат до старта", "Возврата нет"];
const FORBIDDEN = ["Скидки", "Точные сроки", "Гарантированный результат", "Цены, которых нет в описании", "Бесплатные услуги"];

const forbiddenField = (placeholder: string): BusinessField => ({
  key: "forbidden",
  label: "Чего нельзя обещать",
  question: "Чего агенту нельзя обещать клиентам?",
  placeholder,
  suggestions: FORBIDDEN,
});

const EXTRA_FIELD: BusinessField = {
  key: "extra",
  label: "Дополнительно",
  question: "Что ещё важно знать агенту?",
  placeholder: "Частые вопросы и ответы, особые условия, акции…",
  multiline: true,
};

const BUSINESS_TEMPLATES_BASE: BusinessTemplate[] = [
  {
    key: "course",
    label: "Автор курса / эксперт",
    emoji: "🎓",
    role: "менеджер эксперта «{org}»",
    goal:
      "ответить на вопросы о курсе и узнать имя и номер телефона клиента. Если в сведениях есть ссылка на оплату — дай её",
    rules: ["Не обещай гарантированный результат или доход после обучения."],
    fields: [
      { key: "about", label: "Об эксперте", question: "Кто вы и чем занимаетесь?", placeholder: "Нутрициолог с опытом 7 лет, веду блог о питании", required: true },
      { key: "product", label: "Курс", question: "Как называется курс, чему он учит и для кого?", placeholder: "«Худеем без диет» — 6 недель, для мам в декрете", required: true, multiline: true },
      { key: "prices", label: "Тарифы и цены", question: "Какие тарифы и цены?", placeholder: "Базовый — 49 000 ₸, с обратной связью — 99 000 ₸", required: true, multiline: true },
      { key: "format", label: "Формат", question: "Как проходит обучение?", placeholder: "Онлайн: уроки в записи, чат с куратором, разбор домашних заданий", suggestions: ["Онлайн", "Офлайн", "Онлайн и офлайн", "Уроки в записи", "Прямые эфиры", "Чат с куратором", "Проверка домашних заданий", "Доступ навсегда"] },
      { key: "start", label: "Старт", question: "Когда старт?", placeholder: "Ближайший поток — 1 декабря, или доступ сразу после оплаты", suggestions: ["Доступ сразу после оплаты", "Старт по потокам", "Пока не определён"] },
      { key: "buy", label: "Как купить", question: "Как купить курс?", placeholder: "Ссылка на оплату: https://… или менеджер пришлёт ссылку", suggestions: ["Менеджер пришлёт ссылку на оплату", "Оплата через Kaspi"] },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, карта, рассрочка Kaspi Red", suggestions: [...PAYMENT, "Рассрочка Kaspi Red"] },
      { key: "refund", label: "Возврат", question: "Можно ли вернуть деньги?", placeholder: "Вернём деньги в течение 3 дней после старта", suggestions: REFUND },
      { key: "free", label: "Бесплатно", question: "Есть бесплатный вебинар, гайд или пробный урок?", placeholder: "Бесплатный вебинар каждую среду в 20:00", suggestions: ["Бесплатный вебинар", "Гайд", "Пробный урок", "Бесплатных материалов нет"] },
    ],
    forbiddenPlaceholder: "Скидки без промокода, личные консультации вне тарифа",
    questions: ["Сколько стоит курс?", "Чему я научусь?", "Когда старт?", "Подойдёт новичку?", "Есть рассрочка?", "Можно вернуть деньги?"],
  },
  {
    key: "school",
    label: "Онлайн-школа",
    emoji: "🏫",
    role: "менеджер онлайн-школы «{org}»",
    goal:
      "ответить на вопросы об обучении, узнать имя, возраст ученика (если учится ребёнок) и номер телефона и записать на пробный урок",
    fields: [
      { key: "about", label: "О школе", question: "Чему и кого вы обучаете?", placeholder: "Английский для детей 7–14 лет", required: true },
      { key: "prices", label: "Курсы и цены", question: "Какие курсы и сколько они стоят?", placeholder: "Общий английский — 3 месяца, 25 000 ₸ в месяц", required: true, multiline: true, hint: PRICE_HINT },
      { key: "format", label: "Формат занятий", question: "Как проходят занятия?", placeholder: "Онлайн в Zoom, группы до 8 человек, 2 раза в неделю по 60 минут", suggestions: ["Онлайн в Zoom", "В группе", "Индивидуально", "2 раза в неделю", "Записи уроков", "Домашние задания"] },
      { key: "trial", label: "Пробный урок", question: "Есть пробный урок?", placeholder: "Бесплатный, 30 минут — записываем по номеру телефона", suggestions: ["Бесплатный пробный урок", "Платный пробный урок", "Пробного урока нет"] },
      { key: "teachers", label: "Преподаватели", question: "Кто преподаёт?", placeholder: "Преподаватели с сертификатом CELTA, опыт от 3 лет" },
      { key: "result", label: "После обучения", question: "Что ученик получит в конце?", placeholder: "Сертификат и итоговый проект", suggestions: ["Сертификат", "Итоговый проект", "Экзамен"] },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, карта, помесячно или за весь курс сразу", suggestions: [...PAYMENT, "Помесячно", "За весь курс сразу", "Рассрочка Kaspi Red"] },
      { key: "refund", label: "Возврат", question: "Можно ли вернуть деньги?", placeholder: "Вернём за непройденные занятия", suggestions: [...REFUND, "Возврат за непройденные занятия"] },
    ],
    forbiddenPlaceholder: "Скидки, конкретного преподавателя, время занятий без согласования",
    questions: ["Сколько стоит обучение?", "Есть пробный урок?", "Как проходят занятия?", "Подойдёт ребёнку 10 лет?", "Будет сертификат?", "Можно платить помесячно?"],
  },
  {
    key: "marketing",
    label: "Таргетолог / маркетинг-агентство",
    emoji: "📈",
    role: "менеджер «{org}»",
    goal:
      "ответить на вопросы об услугах, узнать нишу и город клиента, его рекламный бюджет и номер телефона и записать на бесплатную консультацию",
    rules: ["Не обещай конкретное количество заявок, продаж или окупаемость рекламы."],
    fields: [
      { key: "about", label: "О нас", question: "Кто вы и для кого настраиваете рекламу?", placeholder: "Таргетолог, реклама в Instagram и TikTok для салонов и онлайн-школ", required: true },
      { key: "prices", label: "Услуги и цены", question: "Какие услуги и сколько они стоят?", placeholder: "Настройка таргета — от 150 000 ₸ в месяц, аудит рекламы — 30 000 ₸", required: true, multiline: true },
      { key: "budget", label: "Рекламный бюджет", question: "Какой нужен рекламный бюджет?", placeholder: "Оплачивается отдельно, рекомендуем от 100 000 ₸ в месяц", suggestions: ["Оплачивается отдельно", "Входит в стоимость", "От 100 000 ₸ в месяц"] },
      { key: "process", label: "Как работаем", question: "Как вы работаете?", placeholder: "Бриф → стратегия → запуск за 3–5 дней → отчёт раз в неделю", suggestions: ["Бриф", "Стратегия", "Запуск за 3–5 дней", "Отчёт раз в неделю"] },
      { key: "cases", label: "Кейсы", question: "Какие у вас кейсы?", placeholder: "Салон красоты — 120 заявок за месяц по 900 ₸", multiline: true },
      { key: "contract", label: "Договор и оплата", question: "Как оформляете договор и оплату?", placeholder: "Договор с ИП и ТОО, предоплата 50%", suggestions: ["Работаем по договору", "С ИП и ТОО", "Предоплата 50%", "Предоплата 100%"] },
      { key: "start", label: "Как начать", question: "Как клиенту начать работу с вами?", placeholder: "Бесплатная консультация 20 минут — оставить номер телефона", suggestions: ["Бесплатная консультация", "Заполнить бриф", "Созвон в Zoom"] },
    ],
    forbiddenPlaceholder: "Сроки окупаемости, скидки",
    questions: ["Сколько стоят ваши услуги?", "Какой нужен рекламный бюджет?", "Сколько будет заявок?", "Есть кейсы в моей нише?", "Работаете по договору?", "Как быстро запуститесь?"],
  },
  {
    key: "beauty",
    label: "Салон красоты / бьюти-мастер",
    emoji: "💅",
    role: "администратор «{org}»",
    goal: "ответить на вопрос клиента и узнать услугу, удобный день и номер телефона, чтобы записать клиента",
    rules: ["Не обещай сразу конкретное время записи — скажи, что проверишь свободные окна и подтвердишь."],
    fields: [
      { key: "about", label: "О салоне", question: "Как называется салон или мастер и где вы находитесь?", placeholder: "Студия «Ноготки», Алматы, ул. Абая 10", required: true },
      { key: "prices", label: "Услуги и цены", question: "Какие услуги и сколько они стоят?", placeholder: "Маникюр с покрытием — 8 000 ₸, педикюр — 10 000 ₸", required: true, multiline: true, hint: PRICE_HINT },
      { key: "schedule", label: "График", question: "Какой у вас график работы?", placeholder: "Ежедневно с 10:00 до 21:00", required: true, suggestions: SCHEDULE },
      { key: "masters", label: "Мастера", question: "Сколько у вас мастеров и какой у них опыт?", placeholder: "4 мастера, опыт от 3 лет" },
      { key: "materials", label: "Материалы", question: "Какими материалами работаете?", placeholder: "Гель-лаки Kodi, одноразовые пилки" },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, наличные, карта", suggestions: PAYMENT },
      { key: "promo", label: "Акции", question: "Есть сейчас акции?", placeholder: "−20% на первый визит до конца месяца", suggestions: ["Скидка на первый визит", "Акций сейчас нет"] },
    ],
    forbiddenPlaceholder: "Скидки вне акций, конкретного мастера без согласования",
    questions: ["Сколько стоит маникюр?", "Есть свободное время на завтра?", "Где вы находитесь?", "Какие материалы используете?", "Можно оплатить Kaspi?", "Есть скидки?"],
  },
  {
    key: "shop",
    label: "Интернет-магазин",
    emoji: "🛍️",
    role: "менеджер интернет-магазина «{org}»",
    goal:
      "помочь выбрать товар и узнать размер или количество, город доставки и номер телефона, чтобы оформить заказ",
    rules: ["Не подтверждай наличие товара сразу — скажи, что проверишь."],
    fields: [
      { key: "about", label: "О магазине", question: "Что вы продаёте?", placeholder: "Женская одежда больших размеров", required: true },
      { key: "prices", label: "Ассортимент и цены", question: "Какой ассортимент и цены?", placeholder: "Платья — от 15 000 ₸, джинсы — от 12 000 ₸. Каталог: https://…", required: true, multiline: true, hint: PRICE_HINT },
      { key: "delivery", label: "Доставка", question: "Как и за сколько доставляете?", placeholder: "По Алматы — 1 000 ₸, 1 день; по Казахстану — 2–5 дней; бесплатно от 30 000 ₸", required: true, multiline: true, suggestions: ["Доставка по городу", "По всему Казахстану", "Казпочта", "СДЭК", "Бесплатно от определённой суммы"] },
      { key: "sizes", label: "Размеры", question: "Какие есть размеры?", placeholder: "От 48 до 60, таблица размеров на сайте" },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, перевод, оплата при получении", suggestions: [...PAYMENT, "Оплата при получении"] },
      { key: "returns", label: "Обмен и возврат", question: "Можно обменять или вернуть товар?", placeholder: "В течение 14 дней, если товар не был в использовании", suggestions: ["Обмен и возврат в течение 14 дней", "Только обмен", "Возврата нет"] },
      { key: "pickup", label: "Самовывоз", question: "Есть самовывоз?", placeholder: "Алматы, ул. Абая 10, с 10:00 до 19:00", suggestions: ["Самовывоза нет"] },
    ],
    forbiddenPlaceholder: "Сроки доставки короче указанных, скидки",
    questions: ["Сколько стоит доставка?", "Есть размер M?", "Как оформить заказ?", "Можно вернуть товар?", "Когда доставите в Астану?", "Есть скидки?"],
  },
  {
    key: "clinic",
    label: "Клиника / стоматология",
    emoji: "🩺",
    role: "администратор клиники «{org}»",
    goal: "ответить на вопрос клиента и узнать нужную услугу, удобный день и номер телефона, чтобы записать на приём",
    rules: [
      "Не ставь диагнозы и не назначай лечение — предлагай записаться на приём.",
      "При сильной боли или угрозе жизни советуй срочно обратиться к врачу или вызвать скорую по номеру 103.",
    ],
    fields: [
      { key: "about", label: "О клинике", question: "Как называется клиника и где она находится?", placeholder: "Стоматология «Улыбка», Алматы, ул. Абая 10", required: true },
      { key: "prices", label: "Услуги и цены", question: "Какие услуги и сколько они стоят?", placeholder: "Консультация — 8 000 ₸, профессиональная чистка — 15 000 ₸", required: true, multiline: true, hint: PRICE_HINT },
      { key: "schedule", label: "График", question: "Какой у вас график работы?", placeholder: "Пн–сб с 9:00 до 20:00", required: true, suggestions: SCHEDULE },
      { key: "doctors", label: "Врачи", question: "Какие врачи принимают?", placeholder: "Терапевт, ортодонт, хирург; опыт от 5 лет" },
      { key: "booking", label: "Запись", question: "Как записаться на приём?", placeholder: "По номеру телефона, администратор подтвердит время", suggestions: ["По номеру телефона", "Администратор подтвердит время", "Онлайн-запись на сайте"] },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, карта, рассрочка", suggestions: [...PAYMENT, "Рассрочка"] },
    ],
    forbiddenPlaceholder: "Результат лечения, точную стоимость до осмотра",
    questions: ["Сколько стоит консультация?", "У меня болит зуб, что делать?", "Можно записаться на субботу?", "Какие врачи принимают?", "Есть рассрочка на лечение?", "Где вы находитесь?"],
  },
  {
    key: "travel",
    label: "Турагентство",
    emoji: "✈️",
    role: "менеджер турагентства «{org}»",
    goal:
      "ответить на вопрос клиента и узнать направление, даты, количество взрослых и детей и номер телефона, чтобы подобрать тур",
    rules: ["Не подтверждай сразу точную цену и наличие мест — скажи, что проверишь по датам."],
    fields: [
      { key: "about", label: "Об агентстве", question: "Как называется агентство, где вы и с какого года работаете?", placeholder: "«Сапар Тур», Алматы, работаем с 2015 года", required: true },
      { key: "prices", label: "Направления и цены", question: "Какие направления и цены?", placeholder: "Турция — от 350 000 ₸ за человека, Умра — от 850 000 ₸. Цена зависит от дат и отеля", required: true, multiline: true, hint: PRICE_HINT },
      { key: "included", label: "Что входит", question: "Что входит в стоимость?", placeholder: "Перелёт, отель, трансфер, страховка", suggestions: ["Перелёт", "Отель", "Трансфер", "Страховка", "Виза", "Питание", "Экскурсии", "Гид"] },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, рассрочка, предоплата 30% для бронирования", suggestions: [...PAYMENT, "Рассрочка", "Предоплата для бронирования"] },
      { key: "office", label: "Офис", question: "Где ваш офис и когда вы работаете?", placeholder: "ул. Абая 10, пн–сб с 10:00 до 19:00" },
    ],
    forbiddenPlaceholder: "Получение визы, скидки",
    questions: ["Сколько стоит тур в Турцию?", "Какие даты есть на Умру?", "Что входит в стоимость?", "Есть рассрочка?", "Нужна ли виза?", "Можно с детьми?"],
  },
  {
    key: "other",
    label: "Другое",
    emoji: "✏️",
    fields: [
      { key: "about", label: "Чем занимаемся", question: "Чем вы занимаетесь и где?", placeholder: "Химчистка мебели и ковров в Алматы", required: true },
      { key: "prices", label: "Услуги и цены", question: "Какие услуги и сколько они стоят?", placeholder: "Химчистка дивана — от 12 000 ₸, ковра — 1 500 ₸ за м²", required: true, multiline: true, hint: PRICE_HINT },
      { key: "schedule", label: "Адрес и график", question: "Где вы находитесь и когда работаете?", placeholder: "Выезжаем на дом, ежедневно с 9:00 до 20:00" },
      { key: "booking", label: "Как записаться или заказать", question: "Как записаться или заказать?", placeholder: "Оставить адрес и номер телефона — перезвоним", suggestions: ["Оставить номер телефона — перезвоним", "Через администратора", "Онлайн-запись"] },
      { key: "payment", label: "Оплата", question: "Как можно оплатить?", placeholder: "Kaspi, наличные", suggestions: PAYMENT },
    ],
    forbiddenPlaceholder: "Скидки, точное время приезда",
    questions: [
      "Здравствуйте! Сколько стоит?",
      "Где вы находитесь?",
      "Какой у вас график работы?",
      "Как можно оплатить?",
      "Как записаться?",
      "Есть скидки?",
    ],
  },
];

// Ко всем шаблонам добавляем «Чего нельзя обещать» и «Дополнительно»
export const BUSINESS_TEMPLATES: BusinessTemplate[] = BUSINESS_TEMPLATES_BASE.map((b) => ({
  ...b,
  fields: [...b.fields, forbiddenField(b.forbiddenPlaceholder), EXTRA_FIELD],
}));

export const getBusinessTemplate = (key: BusinessKey) =>
  BUSINESS_TEMPLATES.find((b) => b.key === key) ?? BUSINESS_TEMPLATES[BUSINESS_TEMPLATES.length - 1];

export type Answers = Record<string, string>;

export const isAnswersComplete = (businessKey: BusinessKey, answers: Answers) =>
  getBusinessTemplate(businessKey).fields.every((f) => !f.required || !!answers[f.key]?.trim());

// ---------- Сборка и разбор промпта ----------

// Эта строка есть только в промптах из мастера — по ней узнаём «свой» промпт
const SIGNATURE = "Используй только сведения о компании ниже.";
const ABOUT = "О компании:\n";
const BUSINESS_LINE = "Сфера деятельности: ";

// Маркер демо-промпта, совпадает с DEMO_AGENT_PROMPT_MARKER на бэкенде
const DEMO_MARKER = "(Это демо-промпт.";

const buildAboutText = (businessKey: BusinessKey, answers: Answers) =>
  getBusinessTemplate(businessKey)
    .fields.filter((f) => answers[f.key]?.trim())
    .map((f) => `${f.label}: ${answers[f.key].trim()}`)
    .join("\n");

export const buildAgentPrompt = (orgName: string, answers: Answers, tone: AgentTone, businessKey: BusinessKey) => {
  const toneLine = TONES.find((t) => t.value === tone)?.line ?? TONES[0].line;
  const business = getBusinessTemplate(businessKey);
  // «{org}» подставляем название организации; если его нет — убираем вместе с кавычками
  const role = (business.role ?? "менеджер компании «{org}»").replace(orgName ? "{org}" : " «{org}»", orgName);
  const goal =
    business.goal ?? "ответить на вопрос клиента и узнать его имя и номер телефона, чтобы оформить заявку";
  const rules = [
    "Общайся как живой менеджер: естественно, тепло, своими словами, без канцелярита и шаблонных фраз.",
    "Ты и есть менеджер. Не говори, что передашь вопрос, заявку или клиента другому менеджеру или специалисту.",
    "Не называй себя ИИ или ботом. Но если клиент прямо спросит, общается ли он с ботом, — ответь честно.",
    `${SIGNATURE} Не выдумывай цены, сроки, скидки и условия.`,
    "Если ответа в сведениях нет — скажи, что уточнишь и напишешь, и попроси номер телефона.",
    ...(business.rules ?? []),
    "Не обсуждай темы, не связанные с компанией.",
  ];
  const businessLine = businessKey !== "other" ? `${BUSINESS_LINE}${business.label}.\n` : "";

  return `Ты — ${role}. Отвечаешь клиентам в Instagram и других мессенджерах.
${businessLine}Отвечай кратко, 1–3 предложения, на языке клиента (русский или казахский). ${toneLine}
Твоя задача: ${goal}.

Правила:
${rules.map((r) => `- ${r}`).join("\n")}

${ABOUT}${buildAboutText(businessKey, answers)}`;
};

export type ParsedPrompt =
  // Промпта нет или он демо — можно смело заполнять
  | { kind: "empty" }
  // Промпт из мастера — достаём ответы, тон и тип бизнеса
  | { kind: "wizard"; answers: Answers; tone: AgentTone; businessKey: BusinessKey }
  // Промпт написан вручную в «Промтинге» — перезаписывать только с подтверждением
  | { kind: "custom" };

// Строки «Подпись: ответ» раскладываем по полям. Строки без известной подписи
// (продолжение многострочного ответа) дописываем к предыдущему полю, а если его нет — в «Дополнительно»
const parseAboutText = (businessKey: BusinessKey, text: string): Answers => {
  const fields = getBusinessTemplate(businessKey).fields;
  const byLabel = new Map(fields.map((f) => [f.label, f.key]));
  const answers: Answers = {};
  let current = "extra";
  for (const line of text.split("\n")) {
    const colon = line.indexOf(": ");
    const key = colon > 0 ? byLabel.get(line.slice(0, colon)) : undefined;
    if (key) {
      current = key;
      answers[key] = line.slice(colon + 2);
    } else {
      answers[current] = answers[current] ? `${answers[current]}\n${line}` : line;
    }
  }
  Object.keys(answers).forEach((k) => (answers[k] = answers[k].trim()));
  return answers;
};

export const parseAgentPrompt = (prompt?: string | null): ParsedPrompt => {
  if (!prompt || !prompt.trim() || prompt.includes(DEMO_MARKER)) {
    return { kind: "empty" };
  }
  const aboutIndex = prompt.indexOf(ABOUT);
  if (!prompt.includes(SIGNATURE) || aboutIndex === -1) {
    return { kind: "custom" };
  }
  const tone = TONES.find((t) => prompt.includes(t.line))?.value ?? "formal";
  const businessKey =
    BUSINESS_TEMPLATES.find((b) => b.key !== "other" && prompt.includes(`${BUSINESS_LINE}${b.label}.`))?.key ?? "other";
  const answers = parseAboutText(businessKey, prompt.slice(aboutIndex + ABOUT.length).trim());
  return { kind: "wizard", answers, tone, businessKey };
};

// ---------- Готовые варианты ответов ----------

const splitAnswer = (value: string) =>
  value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

export const hasSuggestion = (value: string, suggestion: string) => splitAnswer(value).includes(suggestion);

// Добавляет вариант в ответ через запятую или убирает, если он уже есть
export const toggleSuggestion = (value: string, suggestion: string) => {
  const parts = splitAnswer(value);
  return (parts.includes(suggestion) ? parts.filter((p) => p !== suggestion) : [...parts, suggestion]).join(", ");
};
